'use client';

import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import {
  deleteInboxNotification,
  fetchInbox,
  fetchInboxUnreadCount,
  fetchMutedComments,
  fetchMutedSources,
  type InboxNotification,
  type InboxNotificationCategory,
  type InboxPage,
  type InboxQuery,
  type MutedComment,
  type MutedSource,
  type MutedSourceType,
  markAllInboxNotificationsRead,
  markInboxNotificationRead,
  muteComment,
  muteSource,
  type UnreadCount,
  unmuteComment,
  unmuteSource,
} from './inbox-client';

export const INBOX_KEY = ['notifications', 'inbox'] as const;
export const INBOX_LIST_KEY = [...INBOX_KEY, 'list'] as const;
export const INBOX_UNREAD_COUNT_KEY = [...INBOX_KEY, 'unread-count'] as const;
export const MUTED_COMMENTS_KEY = ['notifications', 'mute', 'comments'] as const;
export const MUTED_SOURCES_KEY = ['notifications', 'mute', 'sources'] as const;

const DRAWER_LIMIT = 20;

interface InboxListOptions {
  limit?: number;
  category?: InboxNotificationCategory;
  unread?: boolean;
  enabled?: boolean;
}

export type InboxListQueryKey = readonly [
  'notifications',
  'inbox',
  'list',
  number,
  InboxNotificationCategory | 'all',
  boolean,
];

function inboxListKey(
  opts: Pick<InboxListOptions, 'limit' | 'category' | 'unread'>,
): InboxListQueryKey {
  return [
    ...INBOX_KEY,
    'list',
    opts.limit ?? DRAWER_LIMIT,
    opts.category ?? 'all',
    opts.unread ?? false,
  ] as const;
}

export function useInboxNotifications(options: InboxListOptions = {}) {
  const { isAuthenticated } = useAuthContext();
  const limit = options.limit ?? DRAWER_LIMIT;
  const enabled = options.enabled ?? true;

  return useInfiniteQuery<InboxPage, Error>({
    queryKey: inboxListKey({ limit, category: options.category, unread: options.unread }),
    queryFn: async ({ pageParam }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      const query: InboxQuery = {
        cursor: typeof pageParam === 'string' ? pageParam : undefined,
        limit,
        category: options.category,
        unread: options.unread,
      };
      return fetchInbox(query);
    },
    initialPageParam: undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
    // Keep prior pages visible during category/page switches so the list dims
    // instead of flashing skeletons.
    placeholderData: keepPreviousData,
  });
}

export function useInboxUnreadCount(options: { enabled?: boolean } = {}) {
  const { isAuthenticated } = useAuthContext();
  const enabled = options.enabled ?? true;

  return useQuery<UnreadCount, Error>({
    queryKey: INBOX_UNREAD_COUNT_KEY,
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchInboxUnreadCount();
    },
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export type InboxListData = InfiniteData<InboxPage>;

export function patchInboxListPages(
  data: InboxListData | undefined,
  patch: (item: InboxNotification) => InboxNotification | null,
): InboxListData | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.flatMap((item) => {
        const next = patch(item);
        return next ? [next] : [];
      }),
    })),
  };
}

export function useMarkInboxNotificationRead() {
  const { isAuthenticated } = useAuthContext();
  const qc = useQueryClient();

  return useMutation<void, Error, { id: string; category?: InboxNotificationCategory }>({
    mutationFn: async ({ id }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return markInboxNotificationRead(id);
    },
    onMutate: async ({ id, category }) => {
      let wasUnread = false;
      let resolvedCategory = category;

      qc.setQueriesData<InboxListData>({ queryKey: INBOX_LIST_KEY }, (data) =>
        patchInboxListPages(data, (item) => {
          if (item.id !== id) return item;
          if (!item.read) {
            wasUnread = true;
            resolvedCategory = resolvedCategory ?? item.category;
          }
          return { ...item, read: true };
        }),
      );

      if (wasUnread) {
        qc.setQueryData<UnreadCount>(INBOX_UNREAD_COUNT_KEY, (prev) => {
          if (!prev) return prev;
          const nextByCategory = { ...prev.byCategory };
          if (resolvedCategory) {
            const current = nextByCategory[resolvedCategory] ?? 0;
            const next = Math.max(0, current - 1);
            if (next === 0) delete nextByCategory[resolvedCategory];
            else nextByCategory[resolvedCategory] = next;
          }
          return { count: Math.max(0, prev.count - 1), byCategory: nextByCategory };
        });
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: INBOX_UNREAD_COUNT_KEY });
    },
  });
}

export function useMarkAllInboxNotificationsRead() {
  const { isAuthenticated } = useAuthContext();
  const qc = useQueryClient();

  return useMutation<
    { markedCount: number },
    Error,
    { category?: InboxNotificationCategory } | void
  >({
    mutationFn: async (vars) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return markAllInboxNotificationsRead(vars?.category);
    },
    onMutate: async (vars) => {
      const category = vars?.category;

      qc.setQueriesData<InboxListData>({ queryKey: INBOX_LIST_KEY }, (data) =>
        patchInboxListPages(data, (item) => {
          if (category && item.category !== category) return item;
          return item.read ? item : { ...item, read: true };
        }),
      );

      qc.setQueryData<UnreadCount>(INBOX_UNREAD_COUNT_KEY, (prev) => {
        if (!prev) return prev;
        if (!category) return { count: 0, byCategory: {} };
        const removed = prev.byCategory[category] ?? 0;
        const nextByCategory = { ...prev.byCategory };
        delete nextByCategory[category];
        return { count: Math.max(0, prev.count - removed), byCategory: nextByCategory };
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: INBOX_UNREAD_COUNT_KEY });
      qc.invalidateQueries({ queryKey: INBOX_LIST_KEY });
    },
  });
}

export function useMutedComments(options: { enabled?: boolean } = {}) {
  const { isAuthenticated } = useAuthContext();
  const enabled = options.enabled ?? true;
  return useQuery<MutedComment[], Error>({
    queryKey: MUTED_COMMENTS_KEY,
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchMutedComments();
    },
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
  });
}

export function useMuteComment() {
  const { isAuthenticated } = useAuthContext();
  const qc = useQueryClient();

  return useMutation<void, Error, { commentId: number }>({
    mutationFn: async ({ commentId }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return muteComment(commentId);
    },
    onSuccess: (_data, { commentId }) => {
      // Optimistically drop existing rows for this thread — backend won't push
      // new INBOX_NEW frames for them, but rows already in cache stay until
      // we refetch. Patch locally for instant UX.
      qc.setQueriesData<InboxListData>({ queryKey: INBOX_LIST_KEY }, (data) =>
        patchInboxListPages(data, (item) => (item.commentId === commentId ? null : item)),
      );
      qc.setQueryData<MutedComment[]>(MUTED_COMMENTS_KEY, (prev) => {
        const next = prev ?? [];
        if (next.some((m) => m.commentId === commentId)) return next;
        return [{ commentId, mutedAt: new Date().toISOString() }, ...next];
      });
      qc.invalidateQueries({ queryKey: INBOX_UNREAD_COUNT_KEY });
    },
  });
}

export function useUnmuteComment() {
  const { isAuthenticated } = useAuthContext();
  const qc = useQueryClient();

  return useMutation<void, Error, { commentId: number }>({
    mutationFn: async ({ commentId }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return unmuteComment(commentId);
    },
    onSuccess: (_data, { commentId }) => {
      qc.setQueryData<MutedComment[]>(MUTED_COMMENTS_KEY, (prev) =>
        (prev ?? []).filter((m) => m.commentId !== commentId),
      );
    },
  });
}

export function useMutedSources(options: { enabled?: boolean } = {}) {
  const { isAuthenticated } = useAuthContext();
  const enabled = options.enabled ?? true;
  return useQuery<MutedSource[], Error>({
    queryKey: MUTED_SOURCES_KEY,
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchMutedSources();
    },
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
  });
}

export function useMuteSource() {
  const { isAuthenticated } = useAuthContext();
  const qc = useQueryClient();

  return useMutation<void, Error, { sourceType: MutedSourceType; sourceId: string }>({
    mutationFn: async ({ sourceType, sourceId }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return muteSource(sourceType, sourceId);
    },
    onSuccess: (_data, { sourceType, sourceId }) => {
      // Drop any inbox rows that belong to the just-muted source so the
      // user sees the silence take effect immediately. New events from
      // the backend will already be gated, but cached rows linger.
      qc.setQueriesData<InboxListData>({ queryKey: INBOX_LIST_KEY }, (data) =>
        patchInboxListPages(data, (item) =>
          item.sourceType === sourceType && item.sourceId === sourceId ? null : item,
        ),
      );
      qc.setQueryData<MutedSource[]>(MUTED_SOURCES_KEY, (prev) => {
        const next = prev ?? [];
        if (next.some((m) => m.sourceType === sourceType && m.sourceId === sourceId)) return next;
        return [{ sourceType, sourceId, mutedAt: new Date().toISOString() }, ...next];
      });
      qc.invalidateQueries({ queryKey: INBOX_UNREAD_COUNT_KEY });
    },
  });
}

export function useUnmuteSource() {
  const { isAuthenticated } = useAuthContext();
  const qc = useQueryClient();

  return useMutation<void, Error, { sourceType: MutedSourceType; sourceId: string }>({
    mutationFn: async ({ sourceType, sourceId }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return unmuteSource(sourceType, sourceId);
    },
    onSuccess: (_data, { sourceType, sourceId }) => {
      qc.setQueryData<MutedSource[]>(MUTED_SOURCES_KEY, (prev) =>
        (prev ?? []).filter((m) => !(m.sourceType === sourceType && m.sourceId === sourceId)),
      );
    },
  });
}

export function useDeleteInboxNotification() {
  const { isAuthenticated } = useAuthContext();
  const qc = useQueryClient();

  return useMutation<void, Error, { id: string; category?: InboxNotificationCategory }>({
    mutationFn: async ({ id }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return deleteInboxNotification(id);
    },
    onMutate: async ({ id, category }) => {
      let wasUnread = false;
      let resolvedCategory = category;

      qc.setQueriesData<InboxListData>({ queryKey: INBOX_LIST_KEY }, (data) =>
        patchInboxListPages(data, (item) => {
          if (item.id !== id) return item;
          if (!item.read) {
            wasUnread = true;
            resolvedCategory = resolvedCategory ?? item.category;
          }
          return null;
        }),
      );

      if (wasUnread) {
        qc.setQueryData<UnreadCount>(INBOX_UNREAD_COUNT_KEY, (prev) => {
          if (!prev) return prev;
          const nextByCategory = { ...prev.byCategory };
          if (resolvedCategory) {
            const current = nextByCategory[resolvedCategory] ?? 0;
            const next = Math.max(0, current - 1);
            if (next === 0) delete nextByCategory[resolvedCategory];
            else nextByCategory[resolvedCategory] = next;
          }
          return { count: Math.max(0, prev.count - 1), byCategory: nextByCategory };
        });
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: INBOX_UNREAD_COUNT_KEY });
    },
  });
}
